import { Controller } from "@hotwired/stimulus"
import { savePlan, debounce, matchesServiceLink, migrateCurrentServicesLinks, registerFlush } from "plan_storage"
import { showBulkModal } from "bulk_modal"
import { preserveScroll } from "controllers/scroll_preserve"
import { scheduleRowsHtml, scheduleChipBody, scheduleHasContent } from "schedule_fields"
import { renderIcons } from "lucide_icons"
import { vendorGet } from "vendor_library"

// Custom renderer for the Current Services list. Mirrors the legacy
// editor (pcp_builder.html#5992 + the renderDynamicList branch for
// currentServices). Each row is a green-bordered card that bundles:
//   - reorder arrows (up / down)
//   - link icon
//   - service type pill (dark green) + source-ref badge (Goal X.Y
//     blue / Barrier X.Y amber)
//   - "Live from Goals, Barriers & Working" italic note on the right
//   - one detail row per source resource: RC / Generic pill + comment
//     + optional schedule chips
//   - "Deleted from Goals & Barriers" warning when no source matches
//   - trash button
// Plain (manually-added) rows render the same card chrome but with an
// editable text + tag pill in place of the linked detail block.

const STORAGE_KEY = "pcp_plan_data"

// Schedule chips reuse the CANONICAL formatter (schedule_fields.js) so this list
// reads identically to the goal cards, unmet needs, and the PDF/preview — incl.
// the Assessment-needed / Weekly-Schedule-needed tags. No local copy to drift.
const fmtSchedChipBody = scheduleChipBody

export default class extends Controller {
  static targets = ["list", "empty"]

  connect() {
    this.plan = this._load()
    if (!Array.isArray(this.plan.currentServices)) this.plan.currentServices = []
    // One-time split of a legacy "Other" link (which merged every custom
    // service into one row) into per-name links. Persist + broadcast so the
    // suggestions picker, wizard and preview pick up the split rows.
    if (migrateCurrentServicesLinks(this.plan)) {
      savePlan(this.plan)
      document.dispatchEvent(new CustomEvent("plan:change", {
        bubbles: true, detail: { path: "currentServices", plan: this.plan }
      }))
    }
    this._render()
    this._onChange = (e) => {
      if (this._suppressNextChange) { this._suppressNextChange = false; return }
      // NOTE: we intentionally do NOT skip nested events here — this list
      // shows linked rows derived from goal/barrier resources, so it must
      // re-render when those (nested) sub-edits change. _render() preserves
      // scroll, so re-rendering is not disruptive.
      const p = e.detail?.path || ""
      if (p === "currentServices" || p.startsWith("currentServices.") ||
          p === "goals" || p.startsWith("goals.") ||
          p === "barriers" || p.startsWith("barriers.") ||
          p === "*") {
        this.plan = e.detail.plan || this._load()
        if (!Array.isArray(this.plan.currentServices)) this.plan.currentServices = []
        this._render()
      }
    }
    document.addEventListener("plan:change", this._onChange)
    this._unregFlush = registerFlush(() => this._debSave?.flush())
  }

  disconnect() {
    this._debSave?.flush()
    this._unregFlush?.()
    document.removeEventListener("plan:change", this._onChange)
  }

  add(event) {
    event?.preventDefault?.()
    this.plan.currentServices.push({
      id: Date.now() + Math.random(),
      text: "",
      tag: "general"
    })
    this._save()
    this._render()
  }

  remove(event) {
    event?.preventDefault?.()
    const id = event.currentTarget.dataset.csId
    const row = this.plan.currentServices.find(s => String(s.id) === String(id))
    // Remember a linked (Goals/Barriers) service the user deletes so the
    // auto-populate doesn't immediately bring it back. It still shows in the
    // "Recommended from Goals & Barriers" panel, so it can be re-added on purpose.
    if (row && row.linkedType) this._dismissLink(row.linkedType)
    this.plan.currentServices = this.plan.currentServices.filter(s => String(s.id) !== String(id))
    this._save()
    this._render()
  }

  _dismissLink(type) {
    if (!this.plan.meta) this.plan.meta = {}
    const set = new Set(this.plan.meta.csDismissedLinks || [])
    set.add(type)
    this.plan.meta.csDismissedLinks = [...set]
  }

  moveUp(event)   { this._move(event, -1) }
  moveDown(event) { this._move(event,  1) }
  _move(event, delta) {
    event?.preventDefault?.()
    const id  = event.currentTarget.dataset.csId
    const arr = this.plan.currentServices
    const idx = arr.findIndex(s => String(s.id) === String(id))
    const newIdx = idx + delta
    if (idx < 0 || newIdx < 0 || newIdx >= arr.length) return
    const [item] = arr.splice(idx, 1)
    arr.splice(newIdx, 0, item)
    this._save()
    this._render()
  }

  // Inline edit handlers for the unlinked-row text + tag controls.
  // Linked rows render the source data read-only so they're not wired
  // here.
  updateText(event) {
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    it.text = event.currentTarget.value
    this._saveDebounced()
  }
  // Debounced no-rerender save for per-keystroke text fields (text + schedule
  // inputs). The model is already mutated above; only the persist + broadcast
  // is coalesced. Flushed on disconnect.
  _saveDebounced() {
    ;(this._debSave ||= debounce(() => this._save({ rerender: false }), 200, { dirty: true }))()
  }
  updateTag(event) {
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    it.tag = event.currentTarget.value
    this._save()
    this._render()
  }

  // ---- Vendor picker integration (task 011) ----
  // Handle vendor-picker:change events on plain service rows. The picker
  // fires this when the user selects or adds a vendor from the library.
  handleVendorChange(event) {
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    const { id: vendorId, vendor } = event.detail || {}
    it.vendorId = vendorId || ""
    // Keep the legacy vendor name in sync so old readers (print/preview
    // before task 013) still see something sensible.
    it.vendor = vendor?.name || ""
    this._save()
    this._render()
  }

  // "Link to vendor library" affordance — opens the picker pre-filled with
  // the legacy vendor name so the user can search/add and link it.
  linkLegacyVendor(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    // Set a transient flag so _renderRow shows the picker input pre-filled
    // with the legacy vendor name for search.
    it._linkingVendor = true
    this._render()
    // Focus the newly-rendered input and pre-fill with the legacy name
    requestAnimationFrame(() => {
      const input = this.element.querySelector(
        `.vendor-picker__input[data-cs-id="${CSS.escape(String(id))}"]`
      )
      if (input && it.vendor) {
        input.value = it.vendor
        input.focus()
        input.dispatchEvent(new Event("input", { bubbles: true }))
      }
    })
  }

  // ---- Manual-service Frequency & Schedule ----
  addSchedule(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    if (!Array.isArray(it.schedule)) it.schedule = []
    it.schedule.push({ id: Date.now() + Math.random(), dayType: "Weekly", times: "", unit: "times", hours: "", mins: "" })
    this._save()
    this._render()
  }

  removeSchedule(event) {
    event.preventDefault()
    const id  = event.currentTarget.dataset.csId
    const sid = event.currentTarget.dataset.scheduleId
    const it  = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it || !Array.isArray(it.schedule)) return
    it.schedule = it.schedule.filter(s => String(s.id) !== String(sid))
    this._save()
    this._render()
  }

  updateScheduleField(event) {
    const el  = event.currentTarget
    const it  = this.plan.currentServices.find(s => String(s.id) === String(el.dataset.csId))
    if (!it || !Array.isArray(it.schedule)) return
    const row = it.schedule.find(s => String(s.id) === String(el.dataset.scheduleId))
    if (!row) return
    // TBD checkboxes (Assessment needed / Weekly Schedule needed) swap the
    // hrs/min inputs for the tag — needs a re-render (and clears stale hours).
    if (el.type === "checkbox") {
      row[el.dataset.field] = el.checked
      if (el.checked) { row.hours = ""; row.mins = "" }
      this._saveDebounced()
      this._render()
      return
    }
    row[el.dataset.field] = el.value
    // No re-render: selects show their own value and text inputs keep focus.
    this._saveDebounced()
  }

  clearAll(event) {
    event?.preventDefault?.()
    if (!confirm("Clear every Current Services entry?")) return
    // Dismiss the linked ones too so auto-populate doesn't refill the list the
    // moment it's cleared; they stay available in the "Recommended" panel.
    ;(this.plan.currentServices || []).forEach(s => { if (s.linkedType) this._dismissLink(s.linkedType) })
    this.plan.currentServices = []
    this._save()
    this._render()
  }

  async openBulk(event) {
    event?.preventDefault?.()
    const text = await showBulkModal({
      title:    `Bulk add to "Current Services"`,
      subtitle: `One row per line. Separate columns with "|".`,
      placeholder: "Speech Therapy | rc\nDay Program | general\nRespite Services | rc",
      multi: true,
      fields: ["text", "tag"]
    })
    if (text == null) return
    const lines = text.split("\n").map(s => s.trim()).filter(Boolean)
    if (!lines.length) return
    lines.forEach(line => {
      const parts = line.split("|").map(s => s.trim())
      const rawTag = (parts[1] || "general").toLowerCase()
      this.plan.currentServices.push({
        id: Date.now() + Math.random(),
        text: parts[0] || "",
        tag: (rawTag === "rc" || rawTag === "rc funded" || rawTag === "rcfunded") ? "rc" : "general"
      })
    })
    this._save()
    this._render()
  }

  // ------------------------------------------------------------------
  _render() {
    if (!this.hasListTarget) return
    preserveScroll(this.element, () => {
      const items = this.plan.currentServices || []
      if (this.hasEmptyTarget) this.emptyTarget.hidden = items.length > 0
      this.listTarget.innerHTML = items.map((it, i) => this._renderRow(it, i, items.length)).join("")
      renderIcons(this.element)
    })
  }

  _renderRow(it, idx, total) {
    const id = this._esc(String(it.id))
    const reorder = `
      <div class="cs-reorder">
        <button type="button" class="cs-reorder-btn" ${idx === 0 ? "disabled" : ""}
                data-action="current-services-list#moveUp"  data-cs-id="${id}" title="Move up">
          <i data-lucide="chevron-up"></i>
        </button>
        <button type="button" class="cs-reorder-btn" ${idx === total - 1 ? "disabled" : ""}
                data-action="current-services-list#moveDown" data-cs-id="${id}" title="Move down">
          <i data-lucide="chevron-down"></i>
        </button>
      </div>`
    const trash = `
      <button type="button" class="cs-trash"
              data-action="current-services-list#remove" data-cs-id="${id}" title="Remove">
        <i data-lucide="trash-2"></i>
      </button>`
    if (it.linkedType) {
      const details = this._resolveLinkedDetails(it.linkedType)
      // "Other" services show their typed-in name (the resource's comment)
      // in the header pill instead of the literal "Other" — matches the goal
      // resource editor and the Review/PDF.
      const isOther = it.linkedType === "Other"
      const typeLabel = isOther
        ? (details.map(d => (d.comment || "").trim()).find(Boolean) || "Other")
        : it.linkedType
      return `
        <div class="cs-card cs-card-linked">
          ${reorder}
          <div class="cs-body">
            <div class="cs-head">
              <i data-lucide="link-2" class="cs-link-icon"></i>
              <span class="cs-type-pill">${this._esc(typeLabel)}</span>
              ${[...new Set(details.map(d => d.sourceRef))].map(ref => this._refBadge(ref)).join("")}
              <span class="cs-live">Live from Goals, Barriers &amp; Working</span>
            </div>
            ${details.length === 0
              ? `<div class="cs-deleted">
                   <i data-lucide="alert-triangle"></i>
                   Deleted from Goals &amp; Barriers — consider removing this item.
                 </div>`
              : this._dedupeDetails(details).map(d => this._renderDetail(d, d.isOther)).join("")}
          </div>
          ${trash}
        </div>`
    }
    // Plain (manual) row — editable text + tag pill + vendor picker.
    const tagOpt = (val, label) =>
      `<option value="${val}" ${it.tag === val ? "selected" : ""}>${label}</option>`
    return `
      <div class="cs-card">
        ${reorder}
        <div class="cs-body">
          <div class="cs-row-plain">
            <select class="cs-tag-select"
                    data-action="change->current-services-list#updateTag"
                    data-cs-id="${id}">
              ${tagOpt("general", "Generic")}
              ${tagOpt("rc", "RC Funded")}
            </select>
            <input type="text"
                   class="cs-text-input"
                   data-action="input->current-services-list#updateText"
                   data-cs-id="${id}"
                   value="${this._esc(it.text || "")}"
                   placeholder="Service description">
          </div>
          ${this._vendorPickerBlock(it, id)}
          ${this._scheduleEditor(it, id)}
        </div>
        ${trash}
      </div>`
  }

  // Vendor picker block for a MANUAL current service row.
  // Shows: (a) linked vendor with clear button when vendorId is set,
  //        (b) unlinked legacy name with "link to library" affordance when only
  //            vendor string is present, or
  //        (c) the vendor-picker search input when neither is set or when
  //            the user clicks the link affordance.
  _vendorPickerBlock(it, id) {
    const vendorId = it.vendorId || ""
    const legacyName = (it.vendor || "").trim()
    const vendor = vendorId ? vendorGet(vendorId) : null
    const showLinking = it._linkingVendor === true

    // Case (a): linked vendor — show resolved name + clear
    if (vendorId && vendor) {
      return `
        <div class="cs-vendor-block">
          <label class="cs-vendor-label">Vendor / Provider</label>
          <div class="cs-vendor-linked">
            <i data-lucide="building-2" class="cs-vendor-icon"></i>
            <span class="cs-vendor-name">${this._esc(vendor.name || "(unnamed)")}</span>
            <button type="button" class="cs-vendor-clear"
                    data-action="current-services-list#clearVendor"
                    data-cs-id="${id}" title="Remove vendor link">&times;</button>
          </div>
        </div>`
    }

    // Case (b): legacy name exists but not linked — show name with link affordance
    if (legacyName && !showLinking) {
      return `
        <div class="cs-vendor-block">
          <label class="cs-vendor-label">Vendor / Provider</label>
          <div class="cs-vendor-unlinked">
            <span class="cs-vendor-legacy-name">${this._esc(legacyName)}</span>
            <button type="button" class="cs-vendor-link-btn"
                    data-action="current-services-list#linkLegacyVendor"
                    data-cs-id="${id}" title="Link to vendor library">
              <i data-lucide="link"></i> Link
            </button>
          </div>
        </div>`
    }

    // Case (c): no vendor OR user is linking — show the picker input
    // The vendor-picker controller will be connected by Stimulus when it
    // encounters the data-controller attribute.
    return `
      <div class="cs-vendor-block">
        <label class="cs-vendor-label">Vendor / Provider</label>
        <div class="cs-vendor-picker"
             data-controller="vendor-picker"
             data-vendor-picker-value-value=""
             data-vendor-picker-name-value=""
             data-vendor-picker-placeholder-value="Search or add vendor..."
             data-cs-id="${id}"
             data-action="vendor-picker:change->current-services-list#handleVendorChange">
        </div>
      </div>`
  }

  // Clear the linked vendor, reverting to unlinked state (keeps legacy name
  // if present so the user can re-link).
  clearVendor(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.csId
    const it = this.plan.currentServices.find(s => String(s.id) === String(id))
    if (!it) return
    it.vendorId = ""
    // Keep legacy vendor name so it shows in unlinked state
    this._save()
    this._render()
  }

  // Frequency + Schedule editor for a MANUAL current service — mirrors the
  // goal-resources schedule rows (dayType → # × / sessions → hrs → min) so a
  // manually-added service can carry the same schedule a goal resource does.
  _scheduleEditor(it, id) {
    const rows = scheduleRowsHtml(it.schedule, {
      controller: "current-services-list",
      esc: (s) => this._esc(s),
      rowAttrs: (s) => `data-cs-id="${id}" data-schedule-id="${this._esc(String(s.id))}"`,
      cls: {
        row: "cs-sched-row", daytype: "cs-sched-daytype", num: "cs-sched-num",
        unit: "cs-sched-unit", hrs: "cs-sched-hrs", min: "cs-sched-min",
        lbl: "cs-sched-lbl", del: "cs-sched-del"
      }
    })
    return `
      <div class="cs-sched-block">
        <label class="cs-sched-heading">Frequency &amp; Schedule</label>
        ${rows}
        <button type="button" class="cs-sched-add"
                data-action="current-services-list#addSchedule" data-cs-id="${id}">
          + Add Frequency and Schedule
        </button>
      </div>`
  }

  _renderDetail(d, isOther = false) {
    const tagCls = d.tag === "rc" ? "cs-tag-rc" : "cs-tag-general"
    const tagLbl = d.tag === "rc" ? "RC FUNDED" : "GENERIC"
    const sched = (d.schedule || []).filter(s => scheduleHasContent(s) || s.cost)
    const schedChips = sched.map(s => `
      <span class="cs-sched-chip">
        <span class="cs-sched-d">${this._esc(s.dayType || "")}</span>
        <span class="cs-sched-arr">&rarr;</span>
        <span class="cs-sched-t">${this._esc(fmtSchedChipBody(s))}</span>
      </span>`).join("")
    const ratioPill = (d.ratio && d.ratio !== "1:1")
      ? `<span class="cs-ratio-pill">${this._esc(d.ratio)} ratio</span>` : ""
    // For "Other", the comment IS the service name (now shown in the header
    // pill), so don't repeat it here — show only the vendor/currentServices.
    const body = isOther ? (d.currentServices || "") : (d.comment || d.currentServices || "")
    const chips = (schedChips || ratioPill)
      ? `<div class="cs-detail-chips">${schedChips}${ratioPill}</div>` : ""
    // Bare tag — no body text, schedule, or ratio (common for "Other" services
    // whose whole name lives in the header pill). Render just the tag pill, NOT
    // the 3-column grid row that would leave an empty body cell (the glitch).
    if (!body.trim() && !chips) {
      return `<div class="cs-detail cs-detail-bare"><span class="cs-tag-pill ${tagCls}">${tagLbl}</span></div>`
    }
    return `
      <div class="cs-detail">
        <span class="cs-tag-pill ${tagCls}">${tagLbl}</span>
        <div class="cs-detail-body">${this._esc(body)}</div>
        ${chips}
      </div>`
  }

  // Two goals/barriers can link the SAME service with identical resource
  // details (e.g. both just "RC FUNDED" with no comment / schedule / ratio).
  // The ref badges in the header already list every source (Goal 4, Goal 5);
  // collapse the detail ROWS by their visible content so we don't stack
  // duplicate empty badges.
  _dedupeDetails(details) {
    const seen = new Set()
    const out = []
    for (const d of details || []) {
      const key = [
        d.tag || "",
        (d.comment || "").trim(),
        (d.currentServices || "").trim(),
        d.ratio || "",
        JSON.stringify(d.schedule || [])
      ].join("|")
      if (seen.has(key)) continue
      seen.add(key)
      out.push(d)
    }
    return out
  }

  // Cross-reference badge — now a deep link to the source card. "Goal N"
  // carries the preview's ref-goal anchor so dynamic_list's
  // _scrollToHashGoal scrolls straight to that goal; "Barrier N" opens
  // the Barriers section (no per-barrier anchor exists yet). The 1-based
  // number in the ref string maps to the 0-based render index.
  _refBadge(ref) {
    const isBarrier = /^Barrier/.test(ref)
    const cls = isBarrier ? "cs-ref-barrier" : "cs-ref-goal"
    const m = /(\d+)/.exec(ref)
    const idx = m ? parseInt(m[1], 10) - 1 : -1
    const href = isBarrier
      ? "/editor/native/barriers"
      : (idx >= 0 ? `/editor/native/goals#ref-goal-${idx}` : "/editor/native/goals")
    return `<a href="${this._esc(href)}" class="cs-ref-pill cs-ref-link ${cls}" title="Open ${this._esc(ref)} in the editor">${this._esc(ref)} <span class="cs-ref-ar">&rarr;</span></a>`
  }

  _resolveLinkedDetails(linkedType) {
    const t = String(linkedType || "").trim()
    if (!t) return []
    const out = []
    const push = (r, ref) => {
      out.push({
        sourceRef: ref,
        tag: r.tag || "general",
        comment: r.comment || "",
        currentServices: r.currentServices || "",
        ratio: r.ratio || "",
        schedule: r.schedule || [],
        // Per-resource, so a service named by its comment (an "Other" service)
        // doesn't repeat that name in its own detail body.
        isOther: (r.type || r.text || "").trim() === "Other"
      })
    }
    ;(this.plan.goals || []).forEach((g, gi) => {
      const ref = `Goal ${gi + 1}`
      ;(g.resources || []).forEach(r => {
        if (!r || r.tag === "unmet") return
        if (matchesServiceLink(r, t)) push(r, ref)
      })
    })
    ;(this.plan.barriers || []).forEach((b, bi) => {
      const ref = `Barrier ${bi + 1}`
      ;(b.resources || []).forEach(r => {
        if (!r || r.tag === "unmet") return
        if (matchesServiceLink(r, t)) push(r, ref)
      })
    })
    return out
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
  _load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") }
    catch (_) { return {} }
  }
  _save({ rerender = true } = {}) {
    savePlan(this.plan)
    this._suppressNextChange = true
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "currentServices", plan: this.plan }
    }))
    if (rerender === false) return
  }
}
